import {
  redactSensitiveUrlParamValues,
  SENSITIVE_URL_PARAM_SET,
} from './redaction';

const REDACTED = '[REDACTED]';
const RESPONSE_PREVIEW_LIMIT = 240;
const MAX_DEPTH = 5;
const MAX_ARRAY_ITEMS = 12;
const MAX_OBJECT_KEYS = 24;

const CREDENTIAL_FIELD_NAMES = new Set([
  'authorization',
  'bearer',
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'secret',
  'client_secret',
  'stripe_secret_key',
  'stripe_signature',
  'password',
  'passwd',
  'pwd',
  'cookie',
  'set-cookie',
  'card_number',
  'cardnumber',
  'credit_card',
  'cvv',
  'cvc',
  'pan',
]);

// Keep kiosk API keys, qr_code, ticket_code, reservation_id, session_id, and
// other ENTR scan/session metadata out of the credential sets. They are
// internal operational join keys and must remain visible in Sentry for
// backend, Postgres, and visitor-support correlation.
const CREDENTIAL_URL_PARAMS = SENSITIVE_URL_PARAM_SET;

const CARD_NUMBER_PATTERN = /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{1,7}\b/g;
const CVV_PATTERN = /\b(cvv|cvc|security\s*code)[:\s]*\d{3,4}\b/gi;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9\-_.~+/]+=*/gi;
const JWT_PATTERN =
  /\beyJ[A-Za-z0-9\-_]+\.eyJ[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_.+/=]*\b/g;

export interface ParsedTransportError {
  kind: 'http' | 'network' | 'timeout' | 'abort' | 'unknown';
  message: string;
  statusCode?: number;
  statusText?: string;
  method?: string;
  url?: string;
  responsePreview?: string;
  retryable: boolean;
}

function isCredentialField(key: string): boolean {
  return CREDENTIAL_FIELD_NAMES.has(key.toLowerCase());
}

export function sanitizeStringForTelemetry(value: string): string {
  const sanitized = value
    .replace(CARD_NUMBER_PATTERN, '[CARD_REDACTED]')
    .replace(CVV_PATTERN, '$1:[REDACTED]')
    .replace(BEARER_PATTERN, 'Bearer [TOKEN_REDACTED]')
    .replace(JWT_PATTERN, '[JWT_REDACTED]');
  return redactSensitiveUrlParamValues(sanitized);
}

export function sanitizeForTelemetry(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return sanitizeStringForTelemetry(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'function' || typeof value === 'symbol')
    return undefined;
  if (depth >= MAX_DEPTH) return '[MAX_DEPTH]';

  if (typeof value === 'object') {
    if (seen.has(value)) return '[CIRCULAR]';
    seen.add(value);
  }

  if (Array.isArray(value)) {
    const sanitized = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeForTelemetry(item, depth + 1, seen));
    if (value.length > MAX_ARRAY_ITEMS) {
      sanitized.push(`[+${value.length - MAX_ARRAY_ITEMS} more]`);
    }
    return sanitized;
  }

  const record = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  const keys = Object.keys(record);
  for (const key of keys.slice(0, MAX_OBJECT_KEYS)) {
    result[key] = isCredentialField(key)
      ? REDACTED
      : sanitizeForTelemetry(record[key], depth + 1, seen);
  }
  if (keys.length > MAX_OBJECT_KEYS) {
    result.__truncated__ = `${keys.length - MAX_OBJECT_KEYS} more keys`;
  }
  return result;
}

export function sanitizeUrlForTelemetry(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    for (const key of [...parsed.searchParams.keys()]) {
      if (CREDENTIAL_URL_PARAMS.has(key.toLowerCase())) {
        parsed.searchParams.set(key, REDACTED);
      }
    }
    return parsed.toString();
  } catch {
    return redactSensitiveUrlParamValues(url);
  }
}

export function previewResponseBody(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const raw =
    typeof value === 'string'
      ? value
      : JSON.stringify(sanitizeForTelemetry(value));
  const safe = sanitizeStringForTelemetry(raw);
  if (safe.length <= RESPONSE_PREVIEW_LIMIT) return safe;
  return `${safe.slice(0, RESPONSE_PREVIEW_LIMIT)}...[truncated]`;
}

export function summarizeCodeForTelemetry(
  code: string
): Record<string, unknown> {
  return {
    // ENTR ticket/QR codes are internal operational join keys. Keep the exact
    // value visible in Sentry so scans, backend proxy calls, and support rows
    // can be correlated without tail-only guessing.
    ticketCode: code,
    codeLength: code.length,
  };
}

// Codes the /api/entr/* proxy emits when WE are at fault (token wrong,
// upstream down, body shape broken). Surfacing these to the visitor as
// "QR code not found" would mask a real ops problem and silently route
// users away from staff help. The transport should keep returning
// network-error for these, NOT not-found, regardless of HTTP status.
//
// Source of truth: backend/api/routes/entr.py (ENTR_PROXY_* /
// ENTR_QR_NOT_FOUND ClassVar codes on AppException subclasses).
export const ENTR_PROXY_OPERATOR_ERROR_CODES = new Set([
  'ENTR_PROXY_NO_BACKEND_URL',
  'ENTR_PROXY_NO_TOKEN',
  'ENTR_PROXY_UNAUTHORIZED',
  'ENTR_PROXY_UPSTREAM_5XX',
  'ENTR_PROXY_NON_JSON',
  'ENTR_PROXY_BAD_BODY',
]);

// The single visitor-facing proxy code: "your QR is genuinely unknown".
// Kept separate so the transport can map it to not-found while the
// operator-only codes fall through to network-error.
export const ENTR_PROXY_VISITOR_NOT_FOUND_CODE = 'ENTR_QR_NOT_FOUND';

/**
 * Pull the canonical AppException `code` out of an HttpError.body.
 *
 * The backend's global handler emits `{error, type, code, details?}` for
 * every AppException; the FE's HttpError.body holds that shape verbatim
 * on 4xx/5xx. Returns undefined when the body is missing, non-object,
 * or doesn't carry a string `code` (e.g. Laravel upstream responding
 * directly with `{message: "..."}` — the pre-proxy contract).
 */
export function extractAppExceptionCode(body: unknown): string | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body))
    return undefined;
  const candidate = (body as { code?: unknown }).code;
  return typeof candidate === 'string' && candidate.length > 0
    ? candidate
    : undefined;
}

/**
 * Pull a human-readable message out of an HttpError.body, tolerating both
 * envelope shapes:
 *
 *   - Backend AppException:   {error: "...", type, code, details?}
 *   - Laravel upstream direct: {message: "..."}
 *
 * Used for operator triage in Sentry breadcrumbs + dev console. NEVER
 * render directly in customer UI — strings come straight from the
 * backend or third-party upstream and aren't localized.
 */
export function extractEnvelopeMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body))
    return undefined;
  const record = body as { error?: unknown; message?: unknown };
  // Prefer the AppException `error` field, fall back to upstream-Laravel
  // `message`. A whitespace-only `error` is treated as absent so the
  // upstream-message fallback still wins (defensive — real responses
  // don't do this, but the contract type is `unknown`).
  for (const candidate of [record.error, record.message]) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }
  return undefined;
}

export function isEntrProxyOperatorError(code: string | undefined): boolean {
  return code !== undefined && ENTR_PROXY_OPERATOR_ERROR_CODES.has(code);
}
